import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import axios from '../config/api';
import { useToast } from '../context/ToastContext';
import { formatCurrency } from '../utils/format';

type ScanResult = {
  already_dine_in?: boolean;
  order_number?: string | null;
  total_amount?: number;
  dine_in_discount_percent?: number;
  wallet_credit_jod?: number;
  card_refund_jod?: number;
  card_refunded?: boolean;
  card_refund_note?: string | null;
};

export default function ScanDineIn({
  cafeId,
  onConverted,
}: {
  cafeId?: string;
  onConverted?: () => void;
}) {
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const seenRef = useRef('');

  const stopScanning = async () => {
    await scannerRef.current?.stop().catch(() => {});
    scannerRef.current = null;
    setScanning(false);
  };

  useEffect(() => {
    return () => {
      scannerRef.current?.stop().catch(() => {});
    };
  }, []);

  const submit = async (raw: string) => {
    const value = String(raw || '').trim();
    if (!value || busy) return;
    if (seenRef.current === value) return;
    seenRef.current = value;
    setBusy(true);
    try {
      const { data } = await axios.post<ScanResult>('/api/dine-in/scan', {
        code: value,
        ...(cafeId ? { cafe_id: cafeId } : {}),
      });
      const total = formatCurrency(Number(data.total_amount || 0));
      const label = data.order_number || 'Order';
      if (data.already_dine_in) {
        showToast(`${label} is already dine-in. Total ${total}.`, 'info');
      } else if (data.card_refund_note) {
        showToast(`${label} is dine-in at ${total}. ${data.card_refund_note}`, 'error');
      } else {
        const percent = Number(data.dine_in_discount_percent || 0);
        showToast(
          `${label} is dine-in${percent > 0 ? ` (${percent}% off)` : ''}. New total ${total}.`,
          'success'
        );
      }
      setCode('');
      seenRef.current = '';
      onConverted?.();
    } catch (err: any) {
      seenRef.current = '';
      showToast(err?.response?.data?.error || 'Could not convert that order', 'error');
    } finally {
      setBusy(false);
    }
  };

  const startScanning = async () => {
    try {
      setScanning(true);
      const scanner = new Html5Qrcode('scan-dine-in-camera');
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decoded) => {
          await scanner.stop().catch(() => {});
          scannerRef.current = null;
          setScanning(false);
          await submit(decoded);
        },
        () => {}
      );
    } catch {
      setScanning(false);
      showToast('Could not open the camera. Type the order code instead.', 'error');
    }
  };

  return (
    <div className="mb-4">
      <button
        type="button"
        onClick={() => {
          if (open) void stopScanning();
          setOpen((value) => !value);
        }}
        className="rounded-full bg-[#3d2918] px-4 py-2 text-sm font-semibold text-white"
      >
        Scan dine-in
      </button>
      {open && (
        <div className="mt-3 max-w-lg rounded-2xl border border-amber-200 bg-[#fffcf7] p-4">
          <p className="text-sm text-[#4a3d35]">
            Scan the customer’s order QR, or type the order code. Only this café’s open orders can
            be converted, and only while the café is open.
          </p>
          <div id="scan-dine-in-camera" className="mt-3 overflow-hidden rounded-lg bg-black/5" />
          {!scanning ? (
            <button
              type="button"
              onClick={startScanning}
              className="mt-3 w-full rounded-xl bg-amber-800 py-2.5 text-sm font-semibold text-white"
            >
              Open camera
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void stopScanning()}
              className="mt-3 w-full rounded-xl bg-red-700 py-2.5 text-sm font-semibold text-white"
            >
              Stop camera
            </button>
          )}
          <form
            className="mt-4"
            onSubmit={(event) => {
              event.preventDefault();
              void submit(code);
            }}
          >
            <label className="block text-sm font-semibold text-gray-700">
              Or paste the QR text / type the order code
              <input
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="ORD-0001"
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <button
              type="submit"
              disabled={busy || !code.trim()}
              className="mt-2 rounded-lg border border-amber-800 px-4 py-2 text-sm font-semibold text-amber-900 disabled:opacity-50"
            >
              {busy ? 'Checking…' : 'Convert order'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
